
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CameraFormValues } from "@/types/inventory";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "@/hooks/useTranslation";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { CameraNotesField } from "./form-fields/CameraNotesField";

interface CameraTabsFormProps {
  form: UseFormReturn<CameraFormValues>;
  onSubmit: (values: CameraFormValues) => Promise<void>;
  isEditing?: boolean;
  submitLabel?: string;
}

export function CameraTabsForm({ 
  form, 
  onSubmit, 
  isEditing = false,
  submitLabel
}: CameraTabsFormProps) {
  const [activeTab, setActiveTab] = useState("essential");
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-1">
        <Tabs defaultValue="essential" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-1">
            <TabsTrigger value="essential">{t('essential_info')}</TabsTrigger>
            <TabsTrigger value="optional">{t('optional_details')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="essential" className="space-y-1">
            {/* Essential Information */}
            <div className="space-y-1">
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('brand')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('brand_placeholder')} 
                        {...field} 
                        className={isMobile ? "text-base h-8" : "h-8"}
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('camera_name')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('camera_name_placeholder')} 
                        {...field} 
                        className={isMobile ? "text-base h-8" : "h-8"}
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="camera_type"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('camera_type')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className={`${isMobile ? "text-base" : ""} h-8`}>
                          <SelectValue placeholder={t('select_camera_type')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position={isMobile ? "popper" : "item-aligned"} className={isMobile ? "w-[calc(100vw-2rem)]" : ""}>
                        <SelectItem value="slr">{t('slr')}</SelectItem>
                        <SelectItem value="dslr">{t('dslr')}</SelectItem>
                        <SelectItem value="mirrorless">{t('mirrorless')}</SelectItem>
                        <SelectItem value="rangefinder">{t('rangefinder')}</SelectItem>
                        <SelectItem value="point_and_shoot">{t('point_and_shoot')}</SelectItem>
                        <SelectItem value="instant_film">{t('instant_film')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="format"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('Format')}</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value || ''}
                    >
                      <FormControl>
                        <SelectTrigger className={`${isMobile ? "text-base" : ""} h-8`}>
                          <SelectValue placeholder={t('select_format')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position={isMobile ? "popper" : "item-aligned"} className={isMobile ? "w-[calc(100vw-2rem)]" : ""}>
                        <SelectItem value="full_frame">Full Frame</SelectItem>
                        <SelectItem value="aps-c">APS-C</SelectItem>
                        <SelectItem value="35mm">35mm</SelectItem>
                        <SelectItem value="medium_format">{t('medium_format')}</SelectItem>
                        <SelectItem value="large_format">{t('large_format')}</SelectItem>
                        <SelectItem value="instant">Instant</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="mount"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('Mount')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('mount_placeholder')} 
                        {...field} 
                        className={`uppercase ${isMobile ? "text-base" : ""} h-8`}
                        maxLength={4}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription className="text-xs mt-0">
                      {t('mount_description')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('status')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className={`${isMobile ? "text-base" : ""} h-8`}>
                          <SelectValue placeholder={t('select_status')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position={isMobile ? "popper" : "item-aligned"} className={isMobile ? "w-[calc(100vw-2rem)]" : ""}>
                        <SelectItem value="working">{t('working')}</SelectItem>
                        <SelectItem value="needs_repair">{t('needs_repair')}</SelectItem>
                        <SelectItem value="for_parts">{t('for_parts')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end pt-1">
                <Button type="button" onClick={() => setActiveTab("optional")} size="sm" className="h-8">
                  {t('next_step')}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="optional" className="space-y-1">
            {/* Optional Information */}
            <div className="space-y-1">
              <FormField
                control={form.control}
                name="serial_number"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('serial_number')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        value={field.value || ''} 
                        className={`${isMobile ? "text-base" : ""} h-8`}
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('purchase_date')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          type="date" 
                          {...field} 
                          value={field.value || ''} 
                          className={`${isMobile ? "text-base" : ""} pr-10 h-8`} 
                        />
                        <CalendarDays className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="purchase_price"
                render={({ field }) => (
                  <FormItem className="mb-0">
                    <FormLabel className="text-sm mb-1">{t('purchase_price')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="text" 
                        placeholder="0.00" 
                        {...field} 
                        value={field.value || ''}
                        className={`${isMobile ? "text-base" : ""} h-8`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="mb-0">
                <CameraNotesField form={form} />
              </div>
              
              <div className="flex justify-between pt-1">
                <Button type="button" variant="outline" onClick={() => setActiveTab("essential")} size="sm" className="h-8">
                  {t('back')}
                </Button>
                <Button type="submit" size="sm" className="h-8">
                  {submitLabel || (isEditing ? t('update_camera') : t('add_camera'))}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
}
